import api, { request } from '../../../api';
import { IErrorsStore } from '../../errors/store';
import { IActionGroup, IStore } from '../../index';
import { IAdminEventAwardsStore, IAward } from '../eventAwards/store';

export type ICreateRoundAwardsAction = (
  clubId: number,
  eventId: number,
  roundId: number,
  data: any,
  force?: boolean
) => { adminRoundAwards?: IAdminEventAwardsStore; errors?: IErrorsStore };

export type IDeleteRoundAwardsAction = (
  clubId: number,
  eventId: number,
  roundId: number,
  awardId: number
) => { adminRoundAwards?: IAdminEventAwardsStore; errors?: IErrorsStore };

export type IUpdateRoundAwardsAction = (
  clubId: number,
  eventId: number,
  roundId: number,
  awardId: number,
  award: Partial<IAward>
) => { adminRoundAwards?: IAdminEventAwardsStore; errors?: IErrorsStore };

export interface IAdminRoundAwardsActions extends IActionGroup {
  create: ICreateRoundAwardsAction;
  delete: IDeleteRoundAwardsAction;
  get: (
    clubId: number,
    eventId: number,
    roundId: number,
    force?: boolean
  ) => { adminRoundAwards?: IAdminEventAwardsStore; errors?: IErrorsStore };
  update: IUpdateRoundAwardsAction;
}

const actions: IActionGroup = {
  create: async (state: IStore, clubId: number, eventId: number, roundId: number, data: any) => {
    const response = await request.post(api.admin.roundAwards(clubId, eventId, roundId), data);

    if (response.error) {
      return {
        errors: { adminAwards: { msg: response.error.statusText } }
      };
    }

    return {
      adminRoundAwards: {
        ...state.adminRoundAwards,
        [roundId]: {
          ...state.adminRoundAwards[roundId],
          [response.id]: response
        }
      }
    };
  },
  delete: async (
    state: IStore,
    clubId: number,
    eventId: number,
    roundId: number,
    awardId: number
  ) => {
    const response = await request.delete(api.admin.roundAward(clubId, eventId, roundId, awardId));

    if (response.error) {
      return {
        errors: { adminRoundAwards: { msg: 'Error removing the Round Awards.' } }
      };
    }

    const roundAwards = state.adminRoundAwards[roundId];

    delete roundAwards[awardId];

    return {
      adminRoundAwards: {
        ...state.adminRoundAwards,
        [roundId]: roundAwards
      }
    };
  },
  get: async (state: IStore, clubId: number, eventId: number, roundId: number, force?: boolean) => {
    if (state.adminRoundAwards[roundId] && !force) {
      return {
        adminRoundAwards: state.adminRoundAwards
      };
    }

    const response = await request.get(api.admin.roundAwards(clubId, eventId, roundId));

    if (response.error) {
      return {
        errors: { adminRoundAwards: { msg: 'Error retrieving the Round Awards List.' } }
      };
    }

    return {
      adminRoundAwards: {
        ...state.adminRoundAwards,
        [roundId]: {
          ...response.reduce(
            (acc: IAdminEventAwardsStore, award: IAward) => ({
              ...acc,
              [award.id]: award
            }),
            {}
          )
        }
      }
    };
  },
  update: async (
    state: IStore,
    clubId: number,
    eventId: number,
    roundId: number,
    awardId: number,
    award: Partial<IAward>
  ) => {
    const response = await request.patch(
      api.admin.roundAward(clubId, eventId, roundId, awardId),
      award
    );

    if (response.error) {
      return {
        errors: {
          adminAwards: {
            msg: 'Can`t update the award.'
          }
        }
      };
    }

    return {
      adminRoundAwards: {
        ...state.adminRoundAwards,
        [roundId]: {
          ...state.adminRoundAwards[roundId],
          [response.id]: response
        }
      }
    };
  }
};

export default actions;
